import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
	getReferralInvite,
	normaliseReferralCode,
	type ReferralInvite,
} from "../api/referral";
import { APP_STORE_URL, playStoreUrl } from "../lib/storeLinks";
import "./Referral.css";

const LOGO_URL = "https://plnotimages.z29.web.core.windows.net/pl-appstore.png";

/** "30 days" reads as "1 month" to the same person the app's copy calls it
 *  that. Only whole months are collapsed; anything else stays in days. */
function rewardLabel(days: number): string {
	if (days > 0 && days % 30 === 0) {
		const months = days / 30;
		return `${months} month${months === 1 ? "" : "s"} free`;
	}
	return `${days} days free`;
}

/**
 * Where a shared referral message lands, e.g.
 * https://pl.eeighttech.com/r/UABCD
 *
 * Anonymous and app-state free, like the invoice page. It renders from the
 * code in its own path immediately; the service lookup only upgrades the page
 * with the inviter's first name and the real reward length. That ordering is
 * deliberate — a cold, slow or blocked API call must never leave a recipient
 * looking at a spinner instead of an invitation.
 */
export default function Referral() {
	const { code: rawCode } = useParams<{ code: string }>();
	const code = normaliseReferralCode(rawCode);

	const [invite, setInvite] = useState<ReferralInvite | null>(null);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!code) return;

		let cancelled = false;
		getReferralInvite(code).then((result) => {
			if (!cancelled) setInvite(result);
		});

		return () => {
			cancelled = true;
		};
	}, [code]);

	useEffect(() => {
		if (!copied) return;
		const timer = window.setTimeout(() => setCopied(false), 2200);
		return () => window.clearTimeout(timer);
	}, [copied]);

	// Best effort, once on arrival: both apps now have a Paste button on the
	// activation field, and this is what makes that worth having. Chrome and
	// most in-app webviews allow the write; Safari refuses one that came from
	// no tap, which is why the Copy code button stays and why the steps below
	// do not promise the clipboard is set.
	useEffect(() => {
		if (!code) return;
		navigator.clipboard
			?.writeText(code)
			.then(() => setCopied(true))
			.catch(() => {});
	}, [code]);

	if (!code) {
		return (
			<ReferralShell>
				<h1 className="referral-title">This link doesn't look right</h1>
				<p className="referral-text">
					A referral link carries a short code, for example
					<strong> /r/UABCD</strong>. Ask the person who sent it to share it
					again.
				</p>
				<Link className="referral-secondary" to="/">
					Go to Pocket Ledger
				</Link>
			</ReferralShell>
		);
	}

	const days = invite?.days ?? 30;
	const inviter = invite?.inviter?.trim();

	async function copyCode() {
		try {
			await navigator.clipboard.writeText(code);
			setCopied(true);
		} catch {
			// No clipboard permission (or an old browser). Selecting the code is
			// still possible, and the code is printed on the page — say nothing
			// rather than claiming a copy that did not happen.
			setCopied(false);
		}
	}

	return (
		<ReferralShell>
			<p className="referral-eyebrow">
				{inviter ? `${inviter} invited you` : "You've been invited"}
			</p>

			<h1 className="referral-title">
				Get {rewardLabel(days)} of Pocket Ledger
			</h1>

			<p className="referral-text">
				A ledger, GST invoices and expense tracking for small businesses —
				the bookkeeping app your friend already runs their business on.
			</p>

			<div className="referral-code">
				<span className="referral-code-value">{code}</span>
				<button
					type="button"
					className="referral-copy"
					onClick={copyCode}
					aria-live="polite"
				>
					{copied ? "Copied" : "Copy code"}
				</button>
			</div>

			<div className="referral-stores">
				<a
					className="referral-store referral-store--primary"
					href={APP_STORE_URL}
					target="_blank"
					rel="noopener noreferrer"
				>
					Get it for iPhone
				</a>
				<a
					className="referral-store referral-store--secondary"
					href={playStoreUrl(code, "referral")}
					target="_blank"
					rel="noopener noreferrer"
				>
					Get it for Android
				</a>
			</div>

			<ol className="referral-steps">
				<li>Install Pocket Ledger and sign in.</li>
				{/* The two apps label this row differently, so name both rather
				    than quoting one and sending the other platform's user looking
				    for a button that does not exist. */}
				<li>
					In the app, open the menu → <strong>Upgrade</strong>, then choose{" "}
					<strong>I have a Promo Code</strong> on Android or{" "}
					<strong>Have activation Code</strong> on iPhone.
				</li>
				{/* Not "the code is already on your clipboard": this page copies it
				    on arrival only where the browser allows a write with no tap,
				    and Safari refuses that. Copy code above is the certain route. */}
				<li>
					Tap <strong>Paste</strong> if the code is on your clipboard, or type{" "}
					<strong>{code}</strong>.
				</li>
			</ol>

			<p className="referral-fine">
				Your free months start the moment the code is applied. AI document
				scans are metered separately and are not included.
			</p>
		</ReferralShell>
	);
}

function ReferralShell({ children }: { children: React.ReactNode }) {
	return (
		<div className="referral-page">
			<div className="referral-brand">
				<img src={LOGO_URL} alt="Pocket Ledger" />
				<div className="referral-brand-name">Pocket Ledger</div>
			</div>
			<div className="referral-card">{children}</div>
		</div>
	);
}
